/**
 * CHILD ENTRY FORM CONFIG RESOLVER
 *
 * Merges static YAML configuration (from child-entry-configs.yaml) with
 * runtime-only code (icon components) to produce fully-resolved config objects
 * compatible with the existing interfaces used by ChildEntryForm and related
 * components.
 */

import { resolveLucideIcon } from '@/lib/finance/iconResolver';
import type { EntryTypeConfigYaml, EntryTypeOptionYaml, EntryFieldConfigYaml } from './types';
import type { EntryTypeConfig, EntryFieldConfig } from '@/app/client-zone/modules/total-operations/components/smart-task/components/expense/constants';
import type { ExpenseEntryOption } from '@/app/client-zone/modules/total-operations/components/smart-task/components/EntryTypeSelector';
import type { ChildEntryType, FeatureFlags } from '@/app/client-zone/modules/total-operations/components/smart-task/types';

/**
 * Resolve a YAML entry field config into the runtime EntryFieldConfig.
 * (Direct mapping — no runtime-only fields at present, but kept for symmetry
 * with resolveEntryTypeConfig and future extensibility.)
 */
function resolveEntryField(field: EntryFieldConfigYaml): EntryFieldConfig {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    required: field.required,
    placeholder: field.placeholder,
    options: field.options,
    isCurrency: field.is_currency,
  };
}

/**
 * Resolve a YAML entry type config into a fully-resolved EntryTypeConfig
 * with a real icon component.
 */
export function resolveEntryTypeConfig(
  entryType: string,
  yaml: EntryTypeConfigYaml,
): EntryTypeConfig {
  return {
    label: yaml.label,
    icon: resolveLucideIcon(yaml.icon),
    accentColor: yaml.accent_color,
    fields: yaml.fields.map(resolveEntryField),
  };
}

/**
 * Resolve a YAML entry type option into a fully-resolved ExpenseEntryOption
 * with a real icon component.
 */
export function resolveEntryTypeOption(yaml: EntryTypeOptionYaml): ExpenseEntryOption {
  return {
    type: yaml.type as ChildEntryType,
    label: yaml.label,
    description: yaml.description,
    icon: resolveLucideIcon(yaml.icon),
    color: yaml.color,
    isJobLinked: yaml.is_job_linked,
    featureKey: yaml.feature_key as keyof FeatureFlags | undefined,
  };
}
